import { Injectable } from '@angular/core';
import { Apollo, QueryRef } from 'apollo-angular';
import {
	SendBookingSmsDocument,
	GetAllSmsTemplatesDocument,
	MarkBookingMessagesReadDocument,
	GetSmsTemplatesDocument,
	SmsTemplateInput,
	SaveSmsTemplateDocument,
	RemoveSmsTemplateDocument,
	SendCrewSmsDocument,
	MarkCrewMessagesReadDocument,
	AddSmsTemplateLocationContactDocument,
	SendLocationSmsDocument,
	OrderDirection,
	SendBookingMessageDocument,
	SendCrewMessageDocument,
	WhatsappLoginDocument,
	WhatsappLogoutDocument,
	WhatsappDevicesDocument,
	WhatsappConnectionStatusDocument,
	UserRole,
} from '../../graphql/generated/gen-types';
import { AuthenticationService } from '../authentication/authentication.service';
import { UserService } from '../admin/user.service';
import { BehaviorSubject, firstValueFrom, Observable, of, Subject } from 'rxjs';
import { map, shareReplay, switchMap, take } from 'rxjs/operators';
import { collection, collectionGroup, Firestore, onSnapshot, orderBy, query, where } from '@angular/fire/firestore';
import { TenantSettingService } from '../tenantSetting/tenant-setting.service';

@Injectable({
	providedIn: 'root',
})
export class SmsService {
	currentUser;
	// add subject to store the status of the smsDrawer
	public smsDrawerStatus = new BehaviorSubject<boolean>(false);

	constructor(
		private apollo: Apollo,
		private authenticationService: AuthenticationService,
		private tenantSettingService: TenantSettingService,
		private firestore: Firestore,
		private userService: UserService,
	) {
		this.authenticationService.currentUser.subscribe((user) => {
			this.currentUser = user;
		});
		this.waConnectedSubject = new BehaviorSubject<boolean>(false);

		this.tenantSettingService.getTenantSetting().valueChanges.subscribe(async (result) => {
			const whatsapp = result?.data?.tenantSetting.whatsapp;

			if (!whatsapp || this.userService.getUserProfile?.role === UserRole.Cre) {
				this.waConnectedSubject.next(false);
				return;
			}

			await this.refreshWaConnected();
		});
	}

	// get smsDrawerStatus
	getSmsDrawerStatus(): Observable<boolean> {
		return this.smsDrawerStatus.asObservable();
	}

	// set smsDrawerStatus
	setSmsDrawerStatus(status: boolean) {
		this.smsDrawerStatus.next(status);
	}

	public saveSmsTemplate(smsTemplateInput: SmsTemplateInput, smsTemplateId: string): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: SaveSmsTemplateDocument,
				variables: {
					smsTemplateInput,
					smsTemplateId,
				},
				// 037 FR-029: new-variant creation (null id) must show up in the list
				refetchQueries: [{ query: GetAllSmsTemplatesDocument }],
			})
			.pipe(map((result: any) => result.data));
	}

	// 037 FR-029: soft-remove a template variant (backend keeps the last one per type)
	public removeSmsTemplate(smsTemplateId: string): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: RemoveSmsTemplateDocument,
				variables: { smsTemplateId },
				refetchQueries: [{ query: GetAllSmsTemplatesDocument }],
			})
			.pipe(map((result: any) => result.data));
	}

	public sendBookingSMS(bookingId, smsInput): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: SendBookingSmsDocument,
				variables: {
					bookingId,
					smsInput,
				},
			})
			.pipe(map((result: any) => result.data));
	}

	public sendBookingMessage(bookingId, messageInput): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: SendBookingMessageDocument,
				variables: {
					bookingId,
					messageInput,
				},
			})
			.pipe(map((result: any) => result.data));
	}

	public sendCrewMessage(crewId, messageInput): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: SendCrewMessageDocument,
				variables: {
					crewId,
					messageInput,
				},
			})
			.pipe(map((result: any) => result.data));
	}

	public sendLocationSMS(locationId, flightId, smsInput): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: SendLocationSmsDocument,
				variables: {
					locationId,
					flightId,
					smsInput,
				},
			})
			.pipe(map((result: any) => result.data));
	}

	public sendCrewSms(crewId, smsInput): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: SendCrewSmsDocument,
				variables: {
					crewId,
					smsInput,
				},
			})
			.pipe(map((result: any) => result.data));
	}

	private smsByCrewCache: { [crewId: string]: { observable: Observable<any[]>; unsubscribe: () => void } } = {};
	public smsByCrew(crewId: string): Observable<any[]> {
		if (!this.smsByCrewCache[crewId]) {
			const subject = new Subject<any[]>();

			const tenantSetting$ = this.tenantSettingService.getTenantSetting().valueChanges.pipe(take(1));
			const observable = tenantSetting$.pipe(
				switchMap((result) => {
					let tenantId = result.data?.tenantSetting?.tenantId;
					if (tenantId) {
						const collectionName = `/tenants/${tenantId}/crew/${crewId}/message`;
						const collection_ = collection(this.firestore, collectionName);
						const constraints = [orderBy('date.sms', OrderDirection.Desc)];
						const query_ = query(collection_, ...constraints);

						return new Observable<any[]>((observer) => {
							const unsubscribe = onSnapshot(
								query_,
								(snapshot) => {
									const messages = snapshot.docs.map((doc) => {
										const data = doc.data();
										if (data.date?.sms) {
											data.date.sms = data.date.sms.toDate();
										}
										return data;
									});
									observer.next(messages);
								},
								(error) => observer.error(error)
							);

							return () => unsubscribe();
						});
					} else {
						return of([]);
					}
				}),
				shareReplay(1)
			);

			const subscription = observable.subscribe(subject);
			this.smsByCrewCache[crewId] = { observable: subject.asObservable(), unsubscribe: () => subscription.unsubscribe() };
		}
		return this.smsByCrewCache[crewId].observable;
	}

	public unsubscribeSmsByCrew(crewId: string) {
		if (this.smsByCrewCache[crewId]) {
			this.smsByCrewCache[crewId].unsubscribe();
			delete this.smsByCrewCache[crewId];
		}
	}

	public markBookingMessagesRead(bookingIds: string[]): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: MarkBookingMessagesReadDocument,
				variables: {
					bookingIds,
				},
			})
			.pipe(map((result: any) => result.data));
	}

	public addSmsTemplateLocationContact(): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: AddSmsTemplateLocationContactDocument,
				refetchQueries: [
					{
						query: GetAllSmsTemplatesDocument,
					},
				],
			})
			.pipe(map((result: any) => result.data));
	}

	public markCrewMessagesRead(crewIds: string[]): Observable<any> {
		return this.apollo
			.mutate<any>({
				mutation: MarkCrewMessagesReadDocument,
				variables: {
					crewIds,
				},
			})
			.pipe(map((result: any) => result.data));
	}

	public getAllSmsTemplates(): QueryRef<any> {
		return this.apollo.watchQuery({
			query: GetAllSmsTemplatesDocument,
		});
	}

	public getSmsTemplates(type: string): QueryRef<any> {
		return this.apollo.watchQuery({
			query: GetSmsTemplatesDocument,
			variables: {
				type,
			},
		});
	}

	// FIRESTORE QUERIES TO GET REALTIME SMS DATA
	private unreadSmsCountSubject: BehaviorSubject<number> | null = null;

	public getUnreadSmsCount(): Observable<number> {
		if (!this.unreadSmsCountSubject) {
			this.unreadSmsCountSubject = new BehaviorSubject<number>(0);

			this.tenantSettingService
				.getTenantSetting()
				.valueChanges.pipe(
					take(1),
					switchMap((result) => {
						let tenantId = result.data?.tenantSetting?.tenantId;
						if (tenantId) {
							const collectionGroupName = 'message';
							const constraints = [where('date.read', '==', null), where('tenantId', '==', tenantId), where('direction', '==', 'IN')];
							const collectionGroup_ = collectionGroup(this.firestore, collectionGroupName);
							const query_ = query(collectionGroup_, ...constraints);

							return new Observable<number>((observer) => {
								const unsubscribe = onSnapshot(
									query_,
									(snapshot) => {
										observer.next(snapshot.size);
									},
									(error) => {
										observer.error(error);
									}
								);
								return () => unsubscribe(); // Return unsubscribe function
							});
						} else {
							return of(0); // Handle cases where tenantId is null/undefined
						}
					}),
					shareReplay(1)
				)
				.subscribe(this.unreadSmsCountSubject);
		}
		return this.unreadSmsCountSubject.asObservable();
	}

	private smsByBookingCache: { [bookingId: string]: { observable: Observable<any[]>; unsubscribe: () => void } } = {};
	public smsByBooking(bookingId: string): Observable<any[]> {
		if (!this.smsByBookingCache[bookingId]) {
			const subject = new Subject<any[]>();

			const tenantSetting$ = this.tenantSettingService.getTenantSetting().valueChanges.pipe(take(1));
			const observable = tenantSetting$.pipe(
				switchMap((result) => {
					let tenantId = result.data?.tenantSetting?.tenantId;
					if (tenantId) {
						const collectionName = `/tenants/${tenantId}/booking/${bookingId}/message`;
						const collection_ = collection(this.firestore, collectionName);
						const constraints = [orderBy('date.sms', OrderDirection.Desc)];
						const query_ = query(collection_, ...constraints);

						return new Observable<any[]>((observer) => {
							const unsubscribe = onSnapshot(
								query_,
								(snapshot) => {
									// _docId carries the Firestore doc id for Monty proposal paths (088)
									const smsData: any[] = snapshot.docs.map((doc) => ({ ...(doc.data() as any), _docId: doc.id }));
									const smsDataFiltered = [];

									smsData.forEach((sms) => {
										sms.date.sms = sms.date.sms.toDate(); // Assuming date.sms is a Timestamp

										if (sms.nexmo && sms.nexmo['concat'] === 'true') {
											const concatRef = sms.nexmo['concat-ref'];
											if (!smsDataFiltered.some((s) => s.nexmo && s.nexmo['concat-ref'] === concatRef)) {
												const concatenatedSms = smsData
													.filter((s) => s.nexmo && s.nexmo['concat-ref'] === concatRef)
													.sort((a, b) => a.nexmo['concat-part'] - b.nexmo['concat-part'])
													.reduce((acc, s, index) => {
														if (index === 0) {
															// Start with the first message as the base
															return { ...s, text: s.text };
														}
														// Append the text of subsequent messages
														return { ...acc, text: acc.text + s.text };
													}, null);
												smsDataFiltered.push(concatenatedSms);
											}
										} else {
											smsDataFiltered.push(sms);
										}
									});
									observer.next(smsDataFiltered);
								},
								(error) => observer.error(error)
							);

							return () => unsubscribe();
						});
					} else {
						return of([]);
					}
				}),
				shareReplay(1)
			);

			const subscription = observable.subscribe(subject);
			this.smsByBookingCache[bookingId] = { observable: subject.asObservable(), unsubscribe: () => subscription.unsubscribe() };
		}
		return this.smsByBookingCache[bookingId].observable;
	}

	public unsubscribeSmsByBooking(bookingId: string) {
		if (this.smsByBookingCache[bookingId]) {
			this.smsByBookingCache[bookingId].unsubscribe();
			delete this.smsByBookingCache[bookingId];
		}
	}

	private waConnectedSubject: BehaviorSubject<boolean> | null = null;
	public getWaConnected(): Observable<boolean> {
		return this.waConnectedSubject?.asObservable();
	}

	public setWaConnected(connected: boolean) {
		this.waConnectedSubject.next(connected);
	}

	// Fetch the live connection status and update the shared flag; the flag is otherwise
	// only a snapshot and a transient failure at load time would stick to false (#63)
	public async refreshWaConnected(): Promise<boolean> {
		try {
			const result = await firstValueFrom(
				this.apollo.query<any>({
					query: WhatsappConnectionStatusDocument,
					fetchPolicy: 'network-only',
				})
			);
			const connected = result?.data?.whatsappConnectionStatus?.connected === true;
			this.waConnectedSubject?.next(connected);
			return connected;
		} catch {
			this.waConnectedSubject?.next(false);
			return false;
		}
	}

	public whatsappLogin(): QueryRef<any> {
		return this.apollo.watchQuery({
			query: WhatsappLoginDocument,
			fetchPolicy: 'network-only',
		});
	}
	public whatsappLogout(): QueryRef<any> {
		return this.apollo.watchQuery({
			query: WhatsappLogoutDocument,
			fetchPolicy: 'network-only',
		});
	}
	public whatsappDevices(): QueryRef<any> {
		return this.apollo.watchQuery({
			query: WhatsappDevicesDocument,
			fetchPolicy: 'network-only',
			// Every poll hits the WhatsApp VM devices endpoint; 1s is only needed while QR
			// pairing is in progress — the settings screen raises the rate via startPolling (#166)
			pollInterval: 30000,
		});
	}

	// One-shot on purpose: the caller owns the cadence. A watchQuery with its own
	// pollInterval stacked pollers under the connection-status component's 30s interval (#167)
	public whatsappConnectionStatus(): Observable<any> {
		return this.apollo.query({
			query: WhatsappConnectionStatusDocument,
			fetchPolicy: 'network-only',
		});
	}
}
